import React, { useEffect, useState } from "react";
import styles from "./AccessManagement.module.css";
import axios from "axios";
import commonStyles from '../../common/CommonStyles.module.css';
import { handleApiError, showError } from '../../../utils/errorHandler';

const displayName = (entity) => entity?.displayName || entity?.name || "";

const AccessManagement = () => {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignableRoles, setAssignableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [changingRoleId, setChangingRoleId] = useState(null);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    const fetchAssignableRoles = async () => {
      try {
        const response = await axios.get("/api/users/access/assignable-roles");
        setAssignableRoles(response.data || []);
      } catch (err) {
        setError("Ошибка при загрузке списка ролей");
      }
    };
    fetchAssignableRoles();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setActionMessage("");
    try {
      const response = await axios.get(`/api/users/search?query=${encodeURIComponent(search)}`);
      setUsers(response.data.content || []);
      setSelectedUser(null);
    } catch (err) {
      setError("Ошибка при поиске пользователей");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setActionMessage("");
    setError("");
  };

  const userHasRole = (roleId) => {
    if (!selectedUser || !selectedUser.roles) return false;
    return selectedUser.roles.some((role) => Number(role.id) === Number(roleId));
  };

  const handleToggleRole = async (role) => {
    if (!selectedUser || changingRoleId) return;
    const hasRole = userHasRole(role.id);
    const endpoint = hasRole
      ? "/api/users/access/revoke-role"
      : "/api/users/access/grant-role";
    setLoading(true);
    setChangingRoleId(role.id);
    setError("");
    setActionMessage("");
    try {
      await axios.post(endpoint, { id: selectedUser.id, roleId: role.id });
      setActionMessage(
        hasRole
          ? `Роль снята: «${displayName(role)}»`
          : `Роль назначена: «${displayName(role)}»`
      );
      setSelectedUser((prev) => {
        if (!prev) return prev;
        const roles = prev.roles ? [...prev.roles] : [];
        if (hasRole) {
          return { ...prev, roles: roles.filter((r) => Number(r.id) !== Number(role.id)) };
        }
        return { ...prev, roles: [...roles, role] };
      });
    } catch (err) {
      const errorMessage = handleApiError(err, "Ошибка при изменении роли");
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
      setChangingRoleId(null);
    }
  };

  return (
    <div className={commonStyles.pageContainer}>
      <div className={commonStyles.contentContainer}>
        <div className={commonStyles.content}>
          <h2 style={{ color: 'rgb(151, 1, 14)', textAlign: 'center', fontFamily: 'Ysabeau SC, sans-serif' }}>
            Управление доступами
          </h2>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Поиск по ФИО..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, padding: '0.8rem', border: '2px solid #e0e0e0', borderRadius: 4, fontSize: '1rem' }}
            />
            <button type="submit" disabled={loading} style={{ backgroundColor: '#2196F3', color: 'white', borderRadius: 4, fontWeight: 500, padding: '0.8rem 1.5rem', fontSize: '1rem' }}>
              Найти
            </button>
          </form>
          {error && <div className={styles.error}>{error}</div>}
          {users.length > 0 && (
            <ul className={styles.userList}>
              {users.map((user) => (
                <li
                  key={user.id}
                  className={styles.userItem}
                  onClick={() => handleSelectUser(user)}
                >
                  {user.lastName} {user.firstName} {user.patronymic} ({user.email})
                </li>
              ))}
            </ul>
          )}
          {selectedUser && (
            <div className={styles.userCard}>
              <h3>
                {selectedUser.lastName} {selectedUser.firstName} {selectedUser.patronymic}
              </h3>
              <p>Email: {selectedUser.email}</p>
              <p>Телефон: {selectedUser.phoneNumber}</p>
              <p>
                Текущие роли: {selectedUser.roles && selectedUser.roles.length
                  ? selectedUser.roles.map((r) => displayName(r)).join(", ")
                  : "—"}
              </p>

              <h4 className={styles.rolesTitle}>Назначение ролей</h4>
              {assignableRoles.length ? (
                <div>
                  {assignableRoles.map((role) => {
                    const checked = userHasRole(role.id);
                    const pending = changingRoleId === role.id;
                    return (
                      <label
                        key={role.id}
                        className={`${styles.roleItem}${checked ? " " + styles.roleItemChecked : ""}`}
                        style={{ opacity: pending ? 0.6 : 1 }}
                      >
                        <div className={styles.roleRow}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={loading}
                            onChange={() => handleToggleRole(role)}
                          />
                          <span className={styles.roleName}>{displayName(role)}</span>
                        </div>
                        <div className={styles.privilegesList}>
                          {role.privileges && role.privileges.length ? (
                            role.privileges.map((privilege) => (
                              <span
                                key={privilege.id}
                                className={styles.privilegeTag}
                                title={privilege.name}
                              >
                                {displayName(privilege)}
                              </span>
                            ))
                          ) : (
                            <span className={styles.emptyPrivileges}>Нет привилегий</span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.emptyPrivileges}>Список ролей пуст</div>
              )}

              {actionMessage && <div className={styles.success}>{actionMessage}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessManagement;
